import { ICertificateFullInfo } from '@repo/types';
import { useState } from 'react';
import { Alert, Button } from '@repo/ui';
import { useClaimOwnership } from 'hooks/api/transfers/useClaimOwnership';
import { GenericSuccessDrawer } from 'components/genericSuccessDrawer';

/**
 * VRG-161 — this section used to read the transfer's `code` off the public
 * certificate response (`certificate.transfer.code`) and submit it after a
 * cosmetic scan step. That code is a 4-digit value returned to UNAUTHENTICATED
 * callers (the actual leak this fixes) and is being stripped from every
 * certificate/transfer serializer in this same change. Claiming now goes
 * through the VRG-138 email-bound claim token instead (`claim_token` query
 * param, carried onto this route from the transfer-init email link by
 * `App.tsx` — see the VRG-138 comment there for why it's NOT named `token`,
 * which `ScanTag` already uses for an unrelated mobile-scan-session flow).
 */

export const TransferInActionsSection = ({
  certificate,
  shouldShowOnCertificatePage,
}: {
  certificate: ICertificateFullInfo;
  shouldShowOnCertificatePage: boolean;
}) => {
  const [isSuccessDrawerOpen, setIsSuccessDrawerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // VERA-304: the shared claim path — see `useClaimOwnership` for why the emailed
  // token is not required, and for the fold that reverted this call site once.
  const { claim, isPending } = useClaimOwnership(certificate.id);

  // VERA-304: NOT an early `return null` — see GiftInActionsSection for the full
  // reasoning. Claiming settles the transfer, `shouldShowOnCertificatePage` drops, and
  // unmounting here would take the success drawer rendered below down with it. Gate the
  // pending-state controls instead and always render the drawer.
  const handleClaim = async () => {
    setError(null);
    const result = await claim();
    if (result.ok) {
      setIsSuccessDrawerOpen(true);
      return;
    }
    setError(result.message);
  };

  return (
    <>
      {/* VERA-315 moved the primary's PIXELS into the page's sticky bottom bar;
          VERA-304 replaced what it DOES (VRG-138 token claim, not 315's
          `useAcceptTransfer` + ScanTag scan step). Both kept.

          The primary is also ALWAYS rendered now, mirroring GiftInActionsSection:
          arriving without a `claim_token` used to REPLACE the button with a standing
          sentence, which put a permanent caveat where the action belongs. The sentence
          still exists, on demand, via the same inline `error` line the claim-failure
          path uses. Kept identical to the gift-in section on purpose — these two are
          the same decision on two surfaces, and they were drifting. */}
      {/*
        The action renders HERE, inside the state card, not in a pinned bottom bar.

        It used to portal into the page's StickyActionBar, which put a bare verb a
        full screen away from the block explaining it: "Cancel gift" alone does not
        say which gift, to whom, or that it is still unclaimed — and this card says
        all three. The explanation and the action that resolves it belong together;
        separating them was the defect, and relocating the button was the fix.

        Only the pixels moved. The mutation, the scan-tag drawer and the success
        drawer it drives all still live in this component.
      */}
      {/* FULL WIDTH at the bottom of the card — the shape every state card
          shares, so gift-out, gift-in and transfer-in read as one family rather
          than three arrangements. It stays SECONDARY: this is the card's action,
          not the page's primary CTA, and it is usually a reversal (cancel, deny),
          which a solid fill oversells. */}
        <Button
          className='w-full'
          sx={{ paddingX: 2 }}
          variant='primary'
          /* VERA-304: ALWAYS `handleClaim` — never gated on `claimToken`.
             The VERA-315 `-X theirs` fold reintroduced `claimToken ? handleClaim :
             setError(NO_TOKEN_MESSAGE)` on BOTH incoming sections; the gift half
             was restored and this one was not, so a recipient signed in as the
             transfer's actual recipient pressed Claim and got a red line telling
             them to go find an email. If a future fold reverts this a third time,
             it will show up here and in `GiftInActionsSection` identically — the
             claim itself now lives in `useClaimOwnership`, so there is one
             implementation to restore rather than two to keep in step. */
          onClick={handleClaim}
          disabled={isPending}
        >
          {isPending ? 'Claiming…' : 'Claim ownership'}
        </Button>
      {/* VERA-304: an error is an Alert, not red body text — the same convergence
          GiftInActionsSection documents. Bare `color='error'` prose sits in the
          card's normal text flow with nothing marking it as a status surface, so
          it reads as if the PAGE is broken rather than as a message about the
          last action. This now only ever renders a real server-side rejection. */}
      {error && <Alert severity='error' description={error} sx={{ mt: 2 }} />}
      <GenericSuccessDrawer
        open={isSuccessDrawerOpen}
        onClose={() => setIsSuccessDrawerOpen(false)}
        certificate={certificate}
        drawerTitle='Claim ownership'
        bodyTitle='Ownership claimed'
        description={`You are now the official owner of the ${certificate.product?.title}.`}
        buttonLabel='View certificate'
      />
    </>
  );
};
